import { Box, Disclaimer, Div, H3, lightTheme } from '@vgrubs/components';
import type { AnnouncementViewsPerDay } from '../../../types/api/announcements.types';

type Props = {
  seenCount: number;
  /** A fixed window of days, independent of the chart's range switch. */
  days: AnnouncementViewsPerDay[];
};

/**
 * The announcement at a glance. Only "Seen by" is all time: the backend keeps
 * no running total of views or dismissals, just the daily rows, and those
 * start the day the feature shipped. Summing them and calling it "Total views"
 * would undercount every announcement older than that - so the cards say
 * which range they cover instead.
 */
export const AnnouncementStatCards = ({ seenCount, days }: Props) => {
  const sum = (key: 'views' | 'dismissals') =>
    days.reduce((total, day) => total + day[key], 0);
  const views = sum('views');
  const range = `Last ${days.length} days`;

  const cards = [
    {
      label: 'Seen by',
      value: String(seenCount),
      hint: seenCount === 1 ? 'Person, on any app' : 'People, on any app',
    },
    { label: 'Views', value: String(views), hint: range },
    {
      label: 'Dismissed for good',
      value: String(sum('dismissals')),
      hint: range,
    },
    {
      label: 'Viewers today',
      value: String(days[days.length - 1]?.uniqueViewers ?? 0),
      hint: 'Since midnight',
    },
  ];

  return (
    <Div
      $grid
      $gridTemplateColumns="repeat(4, minmax(0, 1fr))"
      $gridTemplateColumnsSM="repeat(2, minmax(0, 1fr))"
      $gap="16px"
      $gapSM="8px"
    >
      {cards.map((card) => (
        <Box key={card.label} $flex $column $gap="4px">
          <Disclaimer $m="0">{card.label}</Disclaimer>
          <H3 $m="0">{card.value}</H3>
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            {card.hint}
          </Disclaimer>
        </Box>
      ))}
    </Div>
  );
};
